import is from '@sindresorhus/is';
import { z } from 'zod';

import { activityTimelineBlockSchema } from './activity-timeline-block';
import { alignmentBulkRunProgressBlockSchema } from './alignment-bulk-run-progress-block';
import { artifactBlockSchema } from './artifact-block';
import { changeProposalBlockSchema } from './change-proposal-block';
import { clarifyOptionsBlockSchema } from './clarify-options-block';
import { levelMappingProgressBlockSchema } from './level-mapping-progress-block';
import { positionBulkRunProgressBlockSchema } from './position-bulk-run-progress-block';
import { positionPlanProgressBlockSchema } from './position-plan-progress-block';
import { questionnaireBlockSchema } from './questionnaire-block';
import { statusUpdateBlockSchema } from './status-update-block';
import { textBlockSchema } from './text-block';
import { uiActionBlockSchema } from './ui-action-block';

export * from './activity-timeline-block';
export * from './alignment-bulk-run-progress-block';
export * from './artifact-block';
export * from './change-proposal-block';
export * from './clarify-options-block';
export * from './level-mapping-progress-block';
export * from './position-bulk-run-progress-block';
export * from './position-plan-progress-block';
export * from './questionnaire-block';
export * from './rich-text';
export * from './status-update-block';
export * from './text-block';
export * from './ui-action-block';
export * from './ui-block';

const inferBlockType = (input: unknown): unknown => {
    if (!is.plainObject(input)) return input;
    if (is.nonEmptyString(input['type'])) return input;
    if (is.array(input['diffs'])) return { ...input, type: 'change-proposal' };
    if (is.array(input['questions'])) return { ...input, type: 'questionnaire' };
    if (is.array(input['options'])) return { ...input, type: 'clarify-options' };
    if (is.string(input['text'])) return { ...input, type: 'text' };
    return input;
};

export const chatBlockSchema = z.preprocess(
    inferBlockType,
    z.discriminatedUnion('type', [
        activityTimelineBlockSchema,
        artifactBlockSchema,
        textBlockSchema,
        questionnaireBlockSchema,
        changeProposalBlockSchema,
        clarifyOptionsBlockSchema,
        statusUpdateBlockSchema,
        positionPlanProgressBlockSchema,
        positionBulkRunProgressBlockSchema,
        alignmentBulkRunProgressBlockSchema,
        levelMappingProgressBlockSchema,
        uiActionBlockSchema,
    ]),
);

// Unknown or malformed blocks are dropped so a newer backend block type cannot invalidate the whole event or history page.
export const chatBlockListSchema = z.array(z.unknown()).transform((items) =>
    items.flatMap((item) => {
        const result = chatBlockSchema.safeParse(item);
        return result.success ? [result.data] : [];
    }),
);

export type ChatBlock = z.infer<typeof chatBlockSchema>;

export type ChatBlockType = ChatBlock['type'];

const INPUT_REQUEST_BLOCK_TYPES = new Set<ChatBlockType>(['change-proposal', 'clarify-options', 'questionnaire']);

export const isInputRequestBlock = (block: ChatBlock): boolean => INPUT_REQUEST_BLOCK_TYPES.has(block.type);
